import { useNavigate } from "react-router-dom";
import {
  Award,
  Lightbulb,
  Target,
  Users,
  Sparkles,
  ArrowRight,
  Rocket,
  Quote,
} from "lucide-react";
import { useOgMeta } from "@/hooks/useOgMeta";

/**
 * Public, read-only sample Junior CEO Plan — shareable in pitch decks,
 * investor emails, and funder conversations. No auth, no student data.
 * Route: /share/elementary-preview
 */
export default function ElementaryPublicPreview() {
  const navigate = useNavigate();

  useOgMeta({
    title: "Junior CEO Plan — Lemonade Leaders Jr. (Sample)",
    description:
      "A sample one-page Junior CEO Plan from the Lemonade Leaders Jr. pilot — grades 2–5. See what a 3rd grader builds after finishing the missions.",
    image: `${window.location.origin}/api/og/elementary-preview.png`,
  });

  const goPilot = () => navigate("/?pilot=elementary#contact");

  const plan = {
    studentName: "Junior CEO",
    bizBird: { emoji: "🦅", image: "/elementary/birds/eagle.jpg", name: "Eagle", role: "Leader" },
    problem: "Kids lose pencils.",
    businessIdea: "Pencil Power Station",
    customer: "Students who need pencils during class.",
    pitch:
      '"Hi, I\'m a Junior CEO. Kids lose pencils every day. The Pencil Power Station is a friendly corner where students can borrow a sharpened pencil and return it when they\'re done. No more sad, pencil-less classmates!"',
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-amber-50 to-white">
      {/* Top bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-800 font-bold">
            <span className="text-2xl">🍋</span>
            <span>Lemonade Leaders Jr.</span>
          </div>
          <button
            onClick={goPilot}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full font-semibold text-sm transition-colors"
            data-testid="elementary-share-nav-pilot"
          >
            Join the Pilot
          </button>
        </div>
      </nav>

      {/* Header */}
      <header className="pt-28 pb-10 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Sample Junior CEO Plan · Pilot Preview
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 leading-tight">
            What a Junior CEO builds in <span className="text-yellow-500">one adventure.</span>
          </h1>
          <p className="mt-4 text-gray-600 text-lg max-w-3xl">
            This is a sample Lemonade Leaders Jr. plan — the same one-page
            snapshot a real 2nd–5th grader produces after finishing their
            missions. Share it with schools, mentors, or funders to show
            what the pilot actually outputs.
          </p>
        </div>
      </header>

      {/* Plan card */}
      <section className="px-4 pb-14">
        <div className="max-w-4xl mx-auto">
          <div
            className="bg-white rounded-3xl shadow-xl ring-1 ring-yellow-100 overflow-hidden"
            data-testid="elementary-share-plan"
          >
            {/* Banner */}
            <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 text-white p-7 sm:p-9">
              <div className="flex items-center gap-3 mb-3">
                <Award className="w-7 h-7" />
                <span className="uppercase tracking-wide text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">
                  My Junior CEO Plan
                </span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold leading-tight">
                {plan.studentName}
              </h2>
              <p className="mt-1 text-yellow-50">Status: In Progress</p>
            </div>

            {/* Body */}
            <div className="p-7 sm:p-9 space-y-6">
              <dl className="grid sm:grid-cols-2 gap-5">
                <div className="bg-yellow-50 rounded-2xl p-5">
                  <dt className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" /> My Problem
                  </dt>
                  <dd className="text-gray-900 font-medium text-lg">
                    {plan.problem}
                  </dd>
                </div>
                <div className="bg-amber-50 rounded-2xl p-5">
                  <dt className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">
                    My Biz Bird
                  </dt>
                  <dd className="text-gray-900 font-medium text-lg flex items-center gap-2 flex-wrap">
                    {plan.bizBird.image ? (
                      <img
                        src={plan.bizBird.image}
                        alt=""
                        aria-hidden="true"
                        className="w-10 h-10 rounded-full object-contain bg-white ring-1 ring-amber-200"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="text-2xl" aria-hidden="true">
                        {plan.bizBird.emoji}
                      </span>
                    )}
                    <span>{plan.bizBird.name}</span>
                    <span className="text-xs text-amber-700 uppercase tracking-wide font-semibold">
                      {plan.bizBird.role}
                    </span>
                  </dd>
                </div>
                <div className="bg-orange-50 rounded-2xl p-5">
                  <dt className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" /> My Business Idea
                  </dt>
                  <dd className="text-gray-900 font-bold text-lg">
                    {plan.businessIdea}
                  </dd>
                </div>
                <div className="bg-green-50 rounded-2xl p-5">
                  <dt className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> My Customer
                  </dt>
                  <dd className="text-gray-900 font-medium text-lg">
                    {plan.customer}
                  </dd>
                </div>
              </dl>

              {/* Pitch preview */}
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 ring-1 ring-yellow-200 relative">
                <Quote className="absolute top-3 right-3 w-6 h-6 text-yellow-300" />
                <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-3">
                  Pitch Preview
                </p>
                <p className="text-gray-800 italic leading-relaxed">
                  {plan.pitch}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-7 py-4 sm:px-9 text-xs text-gray-500 flex flex-wrap items-center gap-2 justify-between">
              <span>
                Sample plan · no student data · shareable publicly
              </span>
              <span>
                Powered by{" "}
                <span className="font-semibold text-gray-700">LemonBot Jr.</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="px-4 pb-24">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-yellow-500 to-orange-500 rounded-3xl p-8 sm:p-12 text-center text-white shadow-xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Want this for your school or program?
          </h2>
          <p className="text-yellow-50 max-w-2xl mx-auto mb-6">
            Lemonade Leaders Jr. is in pilot for the 2026 cohort. Early
            partners get founder support and help shape the roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/lemonade-leaders-jr")}
              className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-6 py-3 rounded-full font-bold flex items-center justify-center gap-2"
              data-testid="elementary-share-cta-explore"
            >
              Explore Lemonade Leaders Jr.
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={goPilot}
              className="bg-white text-yellow-600 hover:bg-yellow-50 px-6 py-3 rounded-full font-bold flex items-center justify-center gap-2 shadow"
              data-testid="elementary-share-cta-pilot"
            >
              <Rocket className="w-4 h-4" />
              Join the Elementary Pilot List
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
